import React from 'react';
import { FileDown, Copy, Check } from 'lucide-react';
import { useState } from 'react';

const colors = [
  { name: 'Primary Navy', hex: '#122A5C', text: 'white' },
  { name: 'Navy Ink', hex: '#0E214A', text: 'white' },
  { name: 'Ink', hex: '#0B1220', text: 'white' },
  { name: 'Accent Orange', hex: '#E07000', text: 'white' },
  { name: 'White', hex: '#FFFFFF', text: '#122A5C' },
  { name: 'Off White', hex: '#F8FAFC', text: '#122A5C' },
  { name: 'Surface 3', hex: '#F1F5F9', text: '#122A5C' },
  { name: 'Muted Text', hex: '#475569', text: 'white' },
  { name: 'Muted Text 2', hex: '#64748B', text: 'white' },
  { name: 'Border', hex: '#D7DEE9', text: '#122A5C' },
  { name: 'Border Strong', hex: '#B9C6DA', text: '#122A5C' },
  { name: 'Success', hex: '#0F766E', text: 'white' },
  { name: 'Warning', hex: '#B45309', text: 'white' },
  { name: 'Danger', hex: '#B91C1C', text: 'white' },
];

const typographyExamples = [
  { label: 'H1 — Outfit 700', style: { fontFamily: 'Outfit, sans-serif', fontSize: '48px', fontWeight: 700, color: '#122A5C', letterSpacing: '-0.03em', lineHeight: 1.05 }, text: 'IHR ANGEBOT' },
  { label: 'H2 — Outfit 700', style: { fontFamily: 'Outfit, sans-serif', fontSize: '19px', fontWeight: 700, color: '#122A5C', letterSpacing: '-0.01em', lineHeight: 1.15 }, text: 'IHRE VORTEILE AUF EINEN BLICK' },
  { label: 'H3 — Outfit 600', style: { fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 600, color: '#122A5C', lineHeight: 1.2 }, text: 'Garantierte Pachteinnahmen' },
  { label: 'Body — Manrope 400', style: { fontFamily: 'Manrope, sans-serif', fontSize: '14px', fontWeight: 400, color: '#475569', lineHeight: 1.55 }, text: 'EN:POWER ist Ihr spezialisierter Partner für die wirtschaftliche Entwicklung von Photovoltaik-Projekten auf Dach- und Freiflächen.' },
  { label: 'Overline — Manrope 700', style: { fontFamily: 'Manrope, sans-serif', fontSize: '9px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#E07000' }, text: 'EN:POWER • Unternehmensprofil' },
  { label: 'Small/Caption — Manrope 500', style: { fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 500, color: '#94A3B8', letterSpacing: '0.03em' }, text: 'Dieses Angebot ist vertraulich und wurde ausschließlich für den genannten Empfänger erstellt.' },
  { label: 'KPI Number — Outfit 700 Tabular', style: { fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 700, color: '#122A5C', letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }, text: '92.500,-€' },
];

const spacingTokens = [
  { name: 'App Padding', value: 'p-4 md:p-6', px: '16px / 24px' },
  { name: 'Section Padding', value: 'py-10 md:py-14', px: '40px / 56px' },
  { name: 'Canvas Padding', value: 'p-10 md:p-14', px: '40px / 56px' },
  { name: 'Inner Padding (Cards)', value: 'p-6', px: '24px' },
  { name: 'A4 Side Padding', value: '48px', px: '48px' },
  { name: 'A4 Top Padding', value: '32px', px: '32px' },
];

const radiusTokens = [
  { name: 'Cards', value: 'rounded-2xl', px: '16px', visual: '16px' },
  { name: 'Buttons', value: 'rounded-full', px: '9999px', visual: '50px' },
  { name: 'Inputs', value: 'rounded-xl', px: '12px', visual: '12px' },
  { name: 'Small Elements', value: 'rounded-lg', px: '8px', visual: '8px' },
];

const shadowTokens = [
  { name: 'Paper', value: '0 18px 50px rgba(15,23,42,0.12)' },
  { name: 'Toolbar', value: '0 8px 24px rgba(15,23,42,0.08)' },
  { name: 'Card', value: '0 10px 30px rgba(15,23,42,0.06)' },
  { name: 'A4 Page', value: '0 4px 24px rgba(15,23,42,0.18), 0 1px 6px rgba(15,23,42,0.1)' },
];

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handleCopy} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center' }}>
      {copied ? <Check size={12} color="#0F766E" /> : <Copy size={12} color="#94A3B8" />}
    </button>
  );
};

const DESIGN_SYSTEM_JSON = {
  brand: {
    name: "EN:POWER",
    attributes: ["Swiss-inspired clarity", "professional", "technical", "trustworthy", "print-first document craft"],
  },
  colors: {
    primary_navy: "#122A5C",
    accent_orange: "#E07000",
    white: "#FFFFFF",
    off_white: "#F8FAFC",
    ink: "#0B1220",
    navy_ink: "#0E214A",
    muted_text: "#475569",
    muted_text_2: "#64748B",
    border: "#D7DEE9",
    border_strong: "#B9C6DA",
    surface: "#FFFFFF",
    surface_2: "#F8FAFC",
    surface_3: "#F1F5F9",
    success: "#0F766E",
    warning: "#B45309",
    danger: "#B91C1C",
  },
  typography: {
    heading: { family: "Outfit", fallback: "ui-sans-serif, system-ui, -apple-system, sans-serif", weight: "600-800", letterSpacing: "-0.01em to -0.03em" },
    body: { family: "Manrope", fallback: "ui-sans-serif, system-ui, -apple-system, sans-serif", weight: "400-700", lineHeight: "1.55" },
    scale: { h1: "48-68px", h2: "19px", h3: "12-14px", body: "10-14px", small: "8-9px", overline: "9px uppercase tracking-wide" },
  },
  spacing: { app_padding: "16px/24px", section_padding: "40px/56px", canvas_padding: "40px/56px", a4_side: "48px", a4_top: "32px" },
  radius: { cards: "16px", buttons: "9999px", inputs: "12px", small: "8px" },
  shadows: { paper: "0 18px 50px rgba(15,23,42,0.12)", toolbar: "0 8px 24px rgba(15,23,42,0.08)", card: "0 10px 30px rgba(15,23,42,0.06)" },
  borders: { default: "1px solid #D7DEE9", navy_hairline: "1px solid rgba(18,42,92,0.15)", navy_strong: "1px solid rgba(18,42,92,0.25)" },
  a4_dimensions: { width: "210mm (794px)", height: "297mm (1123px)", dpi: "96" },
};

export const DesignSystemPage = () => {
  const handleDownloadJSON = () => {
    const blob = new Blob([JSON.stringify(DESIGN_SYSTEM_JSON, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'enpower-design-system.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 32px 80px' }}>
      {/* Header */}
      <div style={{ marginBottom: '48px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '28px', color: '#122A5C', letterSpacing: '-0.03em' }}>EN:</span>
          <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '28px', color: '#E07000', letterSpacing: '-0.03em' }}>POWER</span>
          <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', marginLeft: '8px' }}>Design System</span>
        </div>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '15px', color: '#475569', lineHeight: 1.6, maxWidth: '600px' }}>
          Vollständige Designsprache für EN:POWER Angebotsvorlagen und Markenkommunikation.
          Swiss-inspired, print-first, technisch-professionell.
        </p>
        <button onClick={handleDownloadJSON} style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px', background: '#122A5C', color: 'white', border: 'none', borderRadius: '999px', padding: '10px 24px', fontFamily: 'Manrope, sans-serif', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          <FileDown size={16} />
          Design Tokens als JSON herunterladen
        </button>
      </div>

      {/* COLORS */}
      <Section title="Farbpalette" subtitle="Primär-, Akzent- und Systemfarben">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
          {colors.map((c, i) => (
            <div key={i} style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #E2E8F0' }}>
              <div style={{ background: c.hex, height: '72px', display: 'flex', alignItems: 'flex-end', padding: '10px 14px' }}>
                <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '22px', fontWeight: 700, color: c.text, fontVariantNumeric: 'tabular-nums' }}>{c.hex}</span>
              </div>
              <div style={{ padding: '10px 14px', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: 600, color: '#334155' }}>{c.name}</span>
                <CopyButton text={c.hex} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* TYPOGRAPHY */}
      <Section title="Typografie" subtitle="Outfit (Headings) + Manrope (Body)">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {typographyExamples.map((t, i) => (
            <div key={i} style={{ padding: '20px 24px', background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t.label}</span>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '9px', color: '#CBD5E1' }}>
                  {t.style.fontSize} / {t.style.fontWeight}
                </span>
              </div>
              <p style={t.style}>{t.text}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* SPACING */}
      <Section title="Abstände" subtitle="Großzügige Weißräume – das Angebot muss atmen wie ein gedrucktes Dokument">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {spacingTokens.map((s, i) => (
            <div key={i} style={{ padding: '16px 20px', background: 'white', borderRadius: '10px', border: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 600, color: '#122A5C', marginBottom: '2px' }}>{s.name}</p>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#64748B' }}>{s.value}</p>
              </div>
              <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '16px', fontWeight: 700, color: '#E07000', fontVariantNumeric: 'tabular-nums' }}>{s.px}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* RADIUS */}
      <Section title="Border Radius" subtitle="Weiche, gerundete Ecken für Karten und Inputs; Buttons sind Pills">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          {radiusTokens.map((r, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ width: '80px', height: '80px', margin: '0 auto 12px', border: '2px solid #122A5C', borderRadius: r.visual, background: '#F8FAFC' }} />
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 600, color: '#122A5C' }}>{r.name}</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#64748B' }}>{r.value}</p>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '12px', fontWeight: 700, color: '#E07000' }}>{r.px}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* SHADOWS */}
      <Section title="Schatten" subtitle="Dezente Schatten für Tiefe und Druckgefühl">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
          {shadowTokens.map((s, i) => (
            <div key={i} style={{ padding: '24px 20px', background: 'white', borderRadius: '16px', boxShadow: s.value, textAlign: 'center' }}>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 600, color: '#122A5C', marginBottom: '6px' }}>{s.name}</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', color: '#94A3B8', wordBreak: 'break-all' }}>{s.value}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* BUTTONS */}
      <Section title="Button-Stile" subtitle="Primär (Orange), Sekundär (Navy Outline), Ghost">
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn-pdf" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <FileDown size={14} /> Primär (Orange)
          </button>
          <button className="btn-docx" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <FileDown size={14} /> Sekundär (Outline)
          </button>
          <button style={{ background: 'transparent', border: 'none', borderRadius: '999px', padding: '8px 18px', fontFamily: 'Manrope, sans-serif', fontSize: '12.5px', fontWeight: 600, color: '#64748B', cursor: 'pointer' }}>
            Ghost Button
          </button>
        </div>
      </Section>

      {/* DO / DON'T */}
      <Section title="Design-Regeln" subtitle="Do's und Don'ts für konsistente Anwendung">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={{ padding: '20px', background: '#F0FDF4', borderRadius: '12px', border: '1px solid #BBF7D0' }}>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 700, color: '#0F766E', marginBottom: '12px' }}>DO</p>
            <ul style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#334155', lineHeight: 2, listStyle: 'none', padding: 0 }}>
              <li>&#10003; Swiss-inspired Klarheit und Struktur</li>
              <li>&#10003; Orange nur als Akzent (CTA, Fokusring, Icons)</li>
              <li>&#10003; Großzügige Weißräume</li>
              <li>&#10003; Outfit für Headings, Manrope für Body</li>
              <li>&#10003; Tabular-Nums für Preise und KPIs</li>
              <li>&#10003; Dezente, zurückhaltende Animationen</li>
            </ul>
          </div>
          <div style={{ padding: '20px', background: '#FEF2F2', borderRadius: '12px', border: '1px solid #FECACA' }}>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 700, color: '#B91C1C', marginBottom: '12px' }}>DON'T</p>
            <ul style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#334155', lineHeight: 2, listStyle: 'none', padding: 0 }}>
              <li>&#10007; Keine transparenten Hintergründe / Glassmorphism</li>
              <li>&#10007; Keine spielerischen/trendigen Visuals</li>
              <li>&#10007; Kein Orange als Flächenfarbe</li>
              <li>&#10007; Keine zentrierten App-Container-Layouts</li>
              <li>&#10007; Keine Emoji-Icons</li>
              <li>&#10007; Keine dunklen Gradients auf Content</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* A4 DIMENSIONS */}
      <Section title="A4 Dokument-Spezifikationen" subtitle="Print-first: Exakte A4-Maße für konsistente PDF-Ausgabe">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {[
            { label: 'Breite', value: '210mm', sub: '794px @ 96dpi' },
            { label: 'Höhe', value: '297mm', sub: '1123px @ 96dpi' },
            { label: 'Seitenränder', value: '48px', sub: 'Seitlich, 32px oben' },
          ].map((d, i) => (
            <div key={i} style={{ padding: '20px', background: '#122A5C', borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>{d.label}</p>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 700, color: 'white', marginBottom: '4px' }}>{d.value}</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>{d.sub}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

const Section = ({ title, subtitle, children }) => (
  <div style={{ marginBottom: '48px' }}>
    <div style={{ marginBottom: '20px', borderBottom: '1px solid #E2E8F0', paddingBottom: '12px' }}>
      <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '20px', fontWeight: 700, color: '#122A5C', letterSpacing: '-0.01em', marginBottom: '4px' }}>{title}</h2>
      <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '13px', color: '#64748B' }}>{subtitle}</p>
    </div>
    {children}
  </div>
);
